import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button, Spinner, Select, Textarea } from '../../components/design-system';
import {
  DEFAULT_PART2,
  PART2_SETTING_KEYS,
  type FollowUpMode,
} from '../../../supabase/functions/_shared/almaPhase2Config.ts';

interface QuestionRow {
  id: string;
  sort_order: number;
  question: string;
  follow_up: FollowUpMode;
  follow_up_hint: string | null;
  active: boolean;
}

const FOLLOW_UP_OPTIONS: { value: FollowUpMode; label: string }[] = [
  { value: 'always', label: 'Always follow up once' },
  { value: 'if_vague', label: 'Follow up only when the answer is vague' },
  { value: 'never', label: 'Never follow up' },
];

/** The settings an admin edits as text, with their defaults and what they do. */
const TEXT_SETTINGS = [
  {
    key: PART2_SETTING_KEYS.rules,
    title: 'Instructions for Part 2',
    help: 'How Alma behaves around the questions. Write {questions} where the list above goes, {budget} for how many there are and {marker} for the counting marker. Alma follows these over the persona prompt during Part 2.',
    fallback: DEFAULT_PART2.rules,
    rows: 16,
  },
  {
    key: PART2_SETTING_KEYS.followUpIfVague,
    title: 'Follow-up when the answer is vague',
    help: 'What Alma does for a question set to "Follow up only when the answer is vague". Keep it about the person, not a specific moment.',
    fallback: DEFAULT_PART2.followUpIfVague,
    rows: 4,
  },
  {
    key: PART2_SETTING_KEYS.archetype,
    title: 'Archetype',
    help: 'What the portrait model is told for the short name above "How Alma sees you" (for example "The Unhurried Noticer").',
    fallback: DEFAULT_PART2.portrait.archetype,
    rows: 5,
  },
  {
    key: PART2_SETTING_KEYS.summary,
    title: '"How Alma sees you"',
    help: 'What the portrait model is told for the paragraph the traveler reads back in their taste map.',
    fallback: DEFAULT_PART2.portrait.summary,
    rows: 9,
  },
  {
    key: PART2_SETTING_KEYS.tripName,
    title: 'Trip name',
    help: 'What the portrait model is told for the name of the trip. Destination names are also removed in code, whatever is written here.',
    fallback: DEFAULT_PART2.portrait.tripName,
    rows: 9,
  },
];

export function AdminAlmaPart2() {
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const [qs, st] = await Promise.all([
      supabase.from('alma_part2_questions').select('*').order('sort_order'),
      supabase.from('alma_part2_settings').select('key, value'),
    ]);
    if (qs.error || st.error) setError("Couldn't load Part 2's settings.");
    setQuestions((qs.data ?? []) as QuestionRow[]);
    setSettings(Object.fromEntries(((st.data ?? []) as { key: string; value: string }[]).map((r) => [r.key, r.value])));
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  /** Runs one save, then shows the result. Reloads so what's shown is what's stored. */
  async function run(id: string, action: () => PromiseLike<{ error: { message: string } | null }>, done: string) {
    setBusy(id);
    setError(null);
    setMessage(null);
    const { error: failure } = await action();
    setBusy(null);
    if (failure) {
      setError(`Couldn't save: ${failure.message}`);
      return;
    }
    setMessage(done);
    await load();
  }

  const patch = (id: string, values: Partial<QuestionRow>) =>
    setQuestions((rows) => rows.map((r) => (r.id === id ? { ...r, ...values } : r)));

  function saveQuestion(q: QuestionRow) {
    if (!q.question.trim()) {
      setError('A question needs some text.');
      return;
    }
    void run(q.id, () => supabase.from('alma_part2_questions').update({
      question: q.question.trim(),
      follow_up: q.follow_up,
      follow_up_hint: q.follow_up === 'always' && q.follow_up_hint?.trim() ? q.follow_up_hint.trim() : null,
      active: q.active,
    }).eq('id', q.id), 'Question saved.');
  }

  function addQuestion() {
    const next = questions.reduce((m, q) => Math.max(m, q.sort_order), -1) + 1;
    void run('new', () => supabase.from('alma_part2_questions').insert({
      sort_order: next, question: 'New question: write what Alma should ask.', follow_up: 'if_vague', active: false,
    }), 'Added, switched off until you turn it on.');
  }

  function move(index: number, by: -1 | 1) {
    const a = questions[index];
    const b = questions[index + by];
    if (!a || !b) return;
    void run(a.id, async () => {
      const first = await supabase.from('alma_part2_questions').update({ sort_order: b.sort_order }).eq('id', a.id);
      if (first.error) return first;
      return supabase.from('alma_part2_questions').update({ sort_order: a.sort_order }).eq('id', b.id);
    }, 'Order saved.');
  }

  function removeQuestion(q: QuestionRow) {
    if (!window.confirm('Delete this question? Turning it off keeps it for later.')) return;
    void run(q.id, () => supabase.from('alma_part2_questions').delete().eq('id', q.id), 'Question deleted.');
  }

  function saveSetting(key: string, value: string) {
    void run(key, () => supabase.from('alma_part2_settings').upsert({ key, value, updated_at: new Date().toISOString() }), 'Saved.');
  }

  if (loading) return <div className="min-h-dvh bg-app-bg flex items-center justify-center"><Spinner /></div>;

  const activeCount = questions.filter((q) => q.active).length;
  const closeAfter = settings[PART2_SETTING_KEYS.closeAfter] ?? '';

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-3xl">
        <h1 className="text-2xl md:text-3xl font-semibold mb-1">Alma Part 2</h1>
        <p className="text-app-muted text-sm mb-8">
          The conversation after the quick questions. Changes apply to the next conversation. Only active questions are
          asked, in this order, and their number is the progress bar's total.
        </p>
        {error && <p className="text-sm text-crimson mb-4">{error}</p>}
        {message && <p className="text-sm text-success mb-4">{message}</p>}

        <h2 className="text-lg font-semibold mb-1">Questions</h2>
        <p className="text-app-muted text-sm mb-5">
          Write each as an instruction: Alma adapts the wording to what the traveler just said but keeps the meaning.
          {activeCount === 0 && ' With none active, Alma uses the built-in set.'}
        </p>
        <div className="flex flex-col gap-4 mb-4">
          {questions.map((q, i) => (
            <div key={q.id} className={`bg-app-card border border-app-border rounded-xl p-4 flex flex-col gap-3 ${q.active ? '' : 'opacity-70'}`}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-app-muted font-medium">
                <span>{q.active ? `Question ${questions.slice(0, i + 1).filter((x) => x.active).length}` : 'Off'}</span>
                <span className="flex-1" />
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0 || busy !== null} className="px-2 py-1 hover:text-app-fg disabled:opacity-30" aria-label="Move up">↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === questions.length - 1 || busy !== null} className="px-2 py-1 hover:text-app-fg disabled:opacity-30" aria-label="Move down">↓</button>
              </div>
              <Textarea
                label="What Alma asks"
                rows={3}
                value={q.question}
                onChange={(e) => patch(q.id, { question: e.target.value })}
              />
              <Select
                label="Follow-up"
                value={q.follow_up}
                onChange={(e) => patch(q.id, { follow_up: e.target.value as FollowUpMode })}
                options={FOLLOW_UP_OPTIONS}
              />
              {q.follow_up === 'always' && (
                <Textarea
                  label="What the follow-up goes after"
                  hint="Optional. Leave empty to let Alma choose."
                  rows={2}
                  value={q.follow_up_hint ?? ''}
                  onChange={(e) => patch(q.id, { follow_up_hint: e.target.value })}
                />
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={q.active} onChange={(e) => patch(q.id, { active: e.target.checked })} />
                Ask this question
              </label>
              <div className="flex items-center gap-3">
                <Button size="sm" onClick={() => saveQuestion(q)} disabled={busy !== null}>Save</Button>
                <button type="button" onClick={() => removeQuestion(q)} disabled={busy !== null} className="text-xs text-crimson/60 hover:text-crimson">Delete</button>
              </div>
            </div>
          ))}
        </div>
        <div className="mb-12">
          <Button variant="secondary" onClick={addQuestion} disabled={busy !== null}>New question</Button>
        </div>

        <h2 className="text-lg font-semibold mb-1">Wrapping up</h2>
        <p className="text-app-muted text-sm mb-4">
          Alma closes when she has what she needs. To keep a long conversation from running on, you can tell her to close
          once the traveler has replied this many times beyond the number of questions. Leave empty to never force it.
        </p>
        <div className="flex items-end gap-3 mb-12">
          <div className="w-40">
            <label htmlFor="part2-close" className="block text-sm font-medium text-app-muted mb-1.5">Extra replies</label>
            <input
              id="part2-close"
              type="number"
              min="0"
              value={closeAfter}
              onChange={(e) => setSettings({ ...settings, [PART2_SETTING_KEYS.closeAfter]: e.target.value })}
              className="w-full bg-app-card border border-app-border rounded-lg px-3 py-2 text-sm"
              placeholder="Never"
            />
          </div>
          <Button size="sm" onClick={() => saveSetting(PART2_SETTING_KEYS.closeAfter, closeAfter.trim())} disabled={busy !== null}>Save</Button>
        </div>

        <h2 className="text-lg font-semibold mb-1">Instructions and taste map</h2>
        <p className="text-app-muted text-sm mb-6">
          The wording Alma and the portrait model work from. Emptying a field restores the built-in text.
        </p>
        {TEXT_SETTINGS.map((s) => {
          const value = settings[s.key] ?? '';
          return (
            <div key={s.key} className="mb-8 flex flex-col gap-2">
              <Textarea
                label={s.title}
                hint={s.help}
                rows={s.rows}
                value={value || s.fallback}
                onChange={(e) => setSettings({ ...settings, [s.key]: e.target.value })}
              />
              <div className="flex items-center gap-3">
                <Button size="sm" onClick={() => saveSetting(s.key, value)} disabled={busy !== null}>Save</Button>
                <button
                  type="button"
                  onClick={() => saveSetting(s.key, '')}
                  disabled={busy !== null}
                  className="text-xs text-app-muted hover:text-app-fg"
                >
                  Restore built-in text
                </button>
              </div>
            </div>
          );
        })}
        <p className="text-app-muted text-xs">
          The question counting marker is not editable: the progress bar relies on Alma using it exactly.
        </p>
      </div>
    </div>
  );
}

export default AdminAlmaPart2;
